import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api, API, apiErrorMessage, formatINR } from "@/lib/api";
import AppHeader from "@/components/AppHeader";
import Footer from "@/components/Footer";
import { LOAN } from "@/constants/testIds";
import { buildReminderMessage } from "@/lib/reminders";
import { toast } from "sonner";
import { motion } from "framer-motion";
import {
  ArrowLeft,
  Copy,
  WhatsappLogo,
  Phone,
  Calendar,
  Percent,
  User,
  ShieldCheck,
  ClockCounterClockwise,
  CheckCircle,
  Trash,
  FilePdf,
  Plus,
  CurrencyInr,
  Lock,
  BellRinging,
  HandCoins,
} from "@phosphor-icons/react";

function StatusChip({ status }) {
  const cls =
    status === "recovered"
      ? "status-recovered"
      : status === "overdue"
      ? "status-overdue"
      : "status-pending";
  return <span className={`lele-chip ${cls}`}>{status}</span>;
}

function ConsentChip({ status }) {
  const cls =
    status === "agreed"
      ? "status-recovered"
      : status === "declined"
      ? "status-overdue"
      : "status-awaiting";
  return <span className={`lele-chip ${cls}`}>{status}</span>;
}

export default function LoanDetailPage() {
  const { id } = useParams();
  const nav = useNavigate();
  const [loan, setLoan] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [payAmount, setPayAmount] = useState("");
  const [payUpiRef, setPayUpiRef] = useState("");
  const [payNote, setPayNote] = useState("");
  const [tone, setTone] = useState("friendly");

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/loans/${id}`);
        setLoan(data);
      } catch (err) {
        toast.error(apiErrorMessage(err));
        nav("/");
      } finally {
        setLoading(false);
      }
    })();
  }, [id, nav]);

  if (loading) {
    return (
      <div className="min-h-screen">
        <AppHeader />
        <div className="p-10 text-neutral-500 text-sm">Loading loan…</div>
      </div>
    );
  }
  if (!loan) return null;

  const viewerIsLender = (loan.viewer_role || "lender") === "lender";
  const payments = loan.payments || [];
  const outstanding = Math.max(
    0,
    (loan.total_repayable || 0) - (loan.amount_paid || 0)
  );
  const progress = Math.min(
    100,
    ((loan.amount_paid || 0) / (loan.total_repayable || 1)) * 100
  );

  const shareUrl = `${window.location.origin}/consent/${loan.share_token}`;
  const message =
    `Hi ${loan.borrower_name}, this is ${loan.lender_name}. ` +
    `I've documented our loan of ${formatINR(loan.amount)} on LeLe. ` +
    `Please review and confirm here: ${shareUrl}`;
  const waHref = loan.borrower_phone
    ? `https://wa.me/${loan.borrower_phone.replace(/\D/g, "")}?text=${encodeURIComponent(message)}`
    : `https://wa.me/?text=${encodeURIComponent(message)}`;

  const copyShare = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      toast.success("Share link copied");
    } catch {
      toast.error("Could not copy — long-press the link instead");
    }
  };

  const submitPayment = async (e) => {
    e.preventDefault();
    const amt = parseFloat(payAmount);
    if (!amt || amt <= 0) {
      toast.error("Enter a valid amount");
      return;
    }
    setSaving(true);
    try {
      const { data } = await api.post(`/loans/${id}/payments`, {
        amount: amt,
        upi_ref: payUpiRef.trim(),
        note: payNote.trim(),
      });
      setLoan(data);
      setPayAmount("");
      setPayUpiRef("");
      setPayNote("");
      toast.success(
        data.computed_status === "recovered"
          ? "Loan fully recovered 🎉"
          : "Payment recorded"
      );
    } catch (err) {
      toast.error(apiErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  const markRecovered = async () => {
    setSaving(true);
    try {
      const { data } = await api.post(`/loans/${id}/mark-recovered`);
      setLoan(data);
      toast.success("Marked as fully recovered");
    } catch (err) {
      toast.error(apiErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  const deletePayment = async (pid) => {
    try {
      const { data } = await api.delete(`/loans/${id}/payments/${pid}`);
      setLoan(data);
      toast.success("Payment removed");
    } catch (err) {
      toast.error(apiErrorMessage(err));
    }
  };

  const consentAgreed = loan.consent_status === "agreed";
  const dueDate = new Date(loan.due_date);
  const createdDate = new Date(loan.created_at);

  return (
    <div className="min-h-screen flex flex-col" data-testid={LOAN.root}>
      <AppHeader />
      <main className="flex-1 mx-auto max-w-4xl px-5 md:px-8 py-8 md:py-12 w-full">
        <button
          data-testid={LOAN.backBtn}
          className="text-sm text-neutral-500 hover:text-neutral-200 flex items-center gap-1.5 mb-6"
          onClick={() => nav("/")}
        >
          <ArrowLeft size={14} weight="bold" /> Back
        </button>

        {!viewerIsLender && (
          <motion.div
            initial={{ opacity: 0, y: -6 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-6 rounded-xl border border-amber-400/25 bg-amber-500/[0.06] px-4 py-3 flex items-center gap-2 text-sm text-amber-200"
          >
            <HandCoins size={16} weight="fill" />
            You are viewing this loan as the <b>borrower</b>. Reach out to the
            lender to log a repayment.
          </motion.div>
        )}

        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-8">
          <div>
            <div className="text-xs uppercase tracking-[0.3em] text-indigo-300 mb-2">
              {viewerIsLender ? `Loan · ${loan.id.slice(0, 8)}` : "Loan you owe"}
            </div>
            <h1 className="font-heading text-3xl md:text-4xl tracking-tight">
              {viewerIsLender ? loan.borrower_name : `From ${loan.lender_name}`}
            </h1>
            <div className="mt-3 flex flex-wrap items-center gap-2">
              <StatusChip status={loan.computed_status} />
              <ConsentChip status={loan.consent_status} />
              {loan.borrower_identity_snapshot && (
                <span
                  data-testid="loan-verified-borrower-chip"
                  className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border border-indigo-400/30 bg-indigo-500/[0.08] text-[11px] text-indigo-200"
                  title={
                    `Verified as ${loan.borrower_identity_snapshot.verified_name} · ` +
                    `${(loan.borrower_identity_snapshot.id_type || "").toUpperCase()} ` +
                    `${loan.borrower_identity_snapshot.id_number_masked || ""}`
                  }
                >
                  <ShieldCheck size={11} weight="fill" />
                  Verified borrower
                </span>
              )}
            </div>
          </div>
          <div className="text-right">
            <div className="text-[11px] uppercase tracking-[0.25em] text-neutral-500">
              Principal
            </div>
            <div className="font-heading text-4xl text-neutral-100">
              {formatINR(loan.amount)}
            </div>
            <div className="text-xs text-neutral-500 mt-1">
              Repayable {formatINR(loan.total_repayable)}
            </div>
          </div>
        </div>

        {/* Repayment progress */}
        <div className="lele-card p-6 md:p-8 mb-8">
          <div className="flex items-center justify-between mb-3 flex-wrap gap-3">
            <div>
              <div className="text-[11px] uppercase tracking-[0.25em] text-neutral-500">
                Repayment progress
              </div>
              <div className="mt-1 flex items-baseline gap-2">
                <span className="font-heading text-2xl text-emerald-300">
                  {formatINR(loan.amount_paid || 0)}
                </span>
                <span className="text-sm text-neutral-500">
                  of {formatINR(loan.total_repayable)}
                </span>
              </div>
              <div className="text-xs text-neutral-500 mt-1">
                Outstanding: <span className="text-neutral-200">{formatINR(outstanding)}</span>
              </div>
            </div>
            {loan.computed_status !== "recovered" && viewerIsLender && (
              <button
                data-testid={LOAN.markRecoveredBtn}
                onClick={markRecovered}
                disabled={saving}
                className="lele-btn-ghost flex items-center gap-2 text-sm"
              >
                <CheckCircle size={16} weight="fill" className="text-emerald-400" />
                Mark as recovered
              </button>
            )}
          </div>
          <div className="h-2 rounded-full bg-white/[0.04] overflow-hidden">
            <motion.div
              initial={{ width: 0 }}
              animate={{ width: `${progress}%` }}
              transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
              className="h-full rounded-full"
              style={{
                background: "linear-gradient(90deg, #10b981 0%, #34d399 100%)",
                boxShadow: "0 0 12px rgba(16,185,129,0.5)",
              }}
            />
          </div>
        </div>

        {/* Payments list + add form */}
        <div className="lele-card p-6 md:p-8 mb-8">
          <div className="flex items-center justify-between mb-4">
            <div className="text-[11px] uppercase tracking-[0.25em] text-neutral-500">
              Payments received
            </div>
            <div className="text-xs text-neutral-500">
              {payments.length} record{payments.length === 1 ? "" : "s"}
            </div>
          </div>

          {payments.length === 0 ? (
            <div className="text-sm text-neutral-500 mb-6">
              No payments yet.
              {viewerIsLender ? " Log the first repayment below." : ""}
            </div>
          ) : (
            <ul className="mb-6 divide-y divide-white/5">
              {payments
                .slice()
                .reverse()
                .map((p) => (
                  <li
                    key={p.id}
                    data-testid={LOAN.paymentRow(p.id)}
                    className="py-3 flex items-center justify-between gap-3"
                  >
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <CurrencyInr
                          size={14}
                          weight="bold"
                          className="text-emerald-400 shrink-0"
                        />
                        <span className="font-heading text-lg text-neutral-100">
                          {formatINR(p.amount)}
                        </span>
                      </div>
                      <div className="text-xs text-neutral-500 mt-0.5 truncate">
                        {new Date(p.paid_at).toLocaleDateString("en-IN", {
                          day: "2-digit",
                          month: "short",
                          year: "numeric",
                        })}
                        {p.upi_ref ? ` · UPI ${p.upi_ref}` : ""}
                        {p.note ? ` · ${p.note}` : ""}
                      </div>
                    </div>
                    {viewerIsLender && (
                      <button
                        data-testid={LOAN.deletePaymentBtn(p.id)}
                        onClick={() => deletePayment(p.id)}
                        className="text-neutral-500 hover:text-rose-400 transition-colors p-2 rounded-lg hover:bg-white/[0.03]"
                        title="Remove payment"
                      >
                        <Trash size={16} weight="bold" />
                      </button>
                    )}
                  </li>
                ))}
            </ul>
          )}

          {loan.computed_status !== "recovered" && viewerIsLender && (
            <form
              onSubmit={submitPayment}
              className="grid grid-cols-1 md:grid-cols-4 gap-3"
            >
              <input
                data-testid={LOAN.addPaymentAmount}
                type="number"
                step="1"
                min="1"
                placeholder="Amount (INR) *"
                value={payAmount}
                onChange={(e) => setPayAmount(e.target.value)}
                required
                className="lele-input rounded-lg px-3 py-2.5 text-sm"
              />
              <input
                data-testid={LOAN.addPaymentUpiRef}
                type="text"
                placeholder="UPI reference"
                value={payUpiRef}
                onChange={(e) => setPayUpiRef(e.target.value)}
                className="lele-input rounded-lg px-3 py-2.5 text-sm"
              />
              <input
                data-testid={LOAN.addPaymentNote}
                type="text"
                placeholder="Note (optional)"
                value={payNote}
                onChange={(e) => setPayNote(e.target.value)}
                className="lele-input rounded-lg px-3 py-2.5 text-sm"
              />
              <button
                data-testid={LOAN.addPaymentSubmit}
                type="submit"
                disabled={saving}
                className="lele-btn-primary flex items-center gap-2 justify-center text-sm"
              >
                <Plus size={14} weight="bold" />
                {saving ? "Saving…" : "Add payment"}
              </button>
            </form>
          )}
        </div>

        {/* Reminder (lender-only) */}
        {loan.computed_status !== "recovered" && viewerIsLender && (
          <ReminderCard
            loan={loan}
            outstanding={outstanding}
            shareUrl={shareUrl}
            tone={tone}
            setTone={setTone}
            onLogged={(entry) =>
              setLoan((prev) => ({
                ...prev,
                reminders: [...(prev.reminders || []), entry],
                last_reminded_at: entry.sent_at,
              }))
            }
          />
        )}

        {/* Facts grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
          <div className="lele-card p-5">
            <div className="text-[11px] uppercase tracking-[0.25em] text-neutral-500 mb-3">
              {viewerIsLender ? "Borrower" : "Lender"}
            </div>
            <div className="flex items-center gap-3 text-neutral-100">
              <User size={18} weight="duotone" className="text-indigo-400" />
              <span>{viewerIsLender ? loan.borrower_name : loan.lender_name}</span>
            </div>
            {loan.borrower_phone && viewerIsLender && (
              <div className="mt-3 flex items-center gap-3 text-neutral-300 text-sm">
                <Phone size={16} weight="duotone" className="text-emerald-400" />
                {loan.borrower_phone}
              </div>
            )}
          </div>
          <div className="lele-card p-5">
            <div className="text-[11px] uppercase tracking-[0.25em] text-neutral-500 mb-3">
              Terms
            </div>
            <div className="flex items-center gap-3 text-sm text-neutral-300">
              <Percent size={16} weight="duotone" className="text-amber-400" />
              {loan.interest_rate}% / yr
            </div>
            <div className="mt-3 flex items-center gap-3 text-sm text-neutral-300">
              <ClockCounterClockwise size={16} weight="duotone" className="text-indigo-400" />
              {loan.tenure_days} day tenure
            </div>
            <div className="mt-3 flex items-center gap-3 text-sm text-neutral-300">
              <Calendar size={16} weight="duotone" className="text-rose-400" />
              Due{" "}
              {dueDate.toLocaleDateString("en-IN", {
                day: "2-digit",
                month: "long",
                year: "numeric",
              })}
            </div>
          </div>
        </div>

        {/* Signed agreements */}
        <div className="lele-card p-6 md:p-8 mb-8 relative overflow-hidden">
          <div className="text-[11px] uppercase tracking-[0.25em] text-neutral-500 mb-3 flex items-center gap-2">
            <FilePdf size={13} weight="fill" className="text-indigo-400" />
            Signed documents
          </div>
          <div className="font-heading text-xl mb-2">
            {consentAgreed
              ? "Your legal documents are ready"
              : "Documents unlock after borrower consent"}
          </div>
          <p className="text-sm text-neutral-400 mb-5 max-w-2xl">
            {consentAgreed
              ? "Both the Loan Agreement and the Demand Promissory Note carry the borrower's timestamped digital consent."
              : "Once the borrower taps Agree on their consent link, LeLe will generate the Loan Agreement and Demand Promissory Note as signed PDFs."}
          </p>
          <div className="flex flex-col sm:flex-row gap-2">
            <a
              data-testid={LOAN.downloadAgreementBtn}
              href={consentAgreed ? `${API}/loans/${loan.id}/agreement.pdf` : undefined}
              target="_blank"
              rel="noreferrer"
              onClick={(e) => {
                if (!consentAgreed) {
                  e.preventDefault();
                  toast.error("Borrower must agree first");
                }
              }}
              className={`flex items-center gap-2 justify-center rounded-full px-5 py-3 text-sm font-medium transition-colors ${
                consentAgreed
                  ? "lele-btn-primary"
                  : "border border-white/10 text-neutral-500 cursor-not-allowed"
              }`}
            >
              {consentAgreed ? (
                <FilePdf size={16} weight="fill" />
              ) : (
                <Lock size={14} weight="bold" />
              )}
              Loan Agreement (PDF)
            </a>
            <a
              data-testid={LOAN.downloadDpnBtn}
              href={consentAgreed ? `${API}/loans/${loan.id}/dpn.pdf` : undefined}
              target="_blank"
              rel="noreferrer"
              onClick={(e) => {
                if (!consentAgreed) {
                  e.preventDefault();
                  toast.error("Borrower must agree first");
                }
              }}
              className={`flex items-center gap-2 justify-center rounded-full px-5 py-3 text-sm font-medium transition-colors ${
                consentAgreed
                  ? "lele-btn-ghost"
                  : "border border-white/10 text-neutral-500 cursor-not-allowed"
              }`}
            >
              {consentAgreed ? (
                <FilePdf size={16} weight="bold" />
              ) : (
                <Lock size={14} weight="bold" />
              )}
              Demand Promissory Note (PDF)
            </a>
          </div>
        </div>

        {/* Share (lender only) */}
        {viewerIsLender && (
          <div className="lele-card p-6 md:p-8 mb-8 relative overflow-hidden">
            <div className="text-[11px] uppercase tracking-[0.25em] text-neutral-500 mb-3 flex items-center gap-2">
              <ShieldCheck size={13} weight="fill" className="text-indigo-400" />
              Borrower consent
            </div>
            <div className="font-heading text-xl mb-2">
              {loan.consent_status === "awaiting"
                ? "Awaiting borrower's confirmation"
                : loan.consent_status === "agreed"
                ? "Borrower agreed to the loan terms"
                : "Borrower declined the loan terms"}
            </div>
            <p className="text-sm text-neutral-400 mb-5 max-w-2xl">
              Share the link below with{" "}
              <span className="text-neutral-200">{loan.borrower_name}</span>. They can
              review the terms and Agree or Decline — no login needed.
            </p>
            <div className="flex flex-col sm:flex-row gap-2">
              <div className="flex-1 lele-input rounded-lg px-4 py-3 text-xs text-neutral-300 break-all font-mono">
                {shareUrl}
              </div>
              <button
                data-testid={LOAN.copyShareBtn}
                onClick={copyShare}
                className="lele-btn-ghost flex items-center gap-2 justify-center"
              >
                <Copy size={16} weight="bold" /> Copy link
              </button>
              <a
                data-testid={LOAN.whatsappBtn}
                href={waHref}
                target="_blank"
                rel="noreferrer"
                className="lele-btn-primary flex items-center gap-2 justify-center"
                style={{
                  background: "linear-gradient(180deg,#25D366 0%,#128C7E 100%)",
                  boxShadow: "0 8px 24px rgba(37,211,102,0.35)",
                }}
              >
                <WhatsappLogo size={16} weight="fill" /> Send on WhatsApp
              </a>
            </div>
          </div>
        )}

        {/* Timeline */}
        <div className="lele-card p-6 md:p-8">
          <div className="text-[11px] uppercase tracking-[0.25em] text-neutral-500 mb-4">
            Timeline
          </div>
          <ol className="relative border-l border-white/10 ml-2 space-y-5">
            <TimelineDot done label="Loan created" time={createdDate.toLocaleString("en-IN")} />
            <TimelineDot
              done={loan.consent_status !== "awaiting"}
              label={
                loan.consent_status === "declined"
                  ? "Consent declined"
                  : loan.consent_status === "agreed"
                  ? "Consent received"
                  : "Awaiting borrower consent"
              }
              time={
                loan.consent_at
                  ? new Date(loan.consent_at).toLocaleString("en-IN")
                  : "Pending borrower action"
              }
            />
            <TimelineDot
              done={payments.length > 0}
              label={
                payments.length > 0
                  ? `${payments.length} payment${payments.length === 1 ? "" : "s"} received`
                  : "First payment awaited"
              }
              time={
                payments.length > 0
                  ? new Date(payments[payments.length - 1].paid_at).toLocaleString("en-IN")
                  : "—"
              }
            />
            <TimelineDot
              done={loan.computed_status === "recovered"}
              label={
                loan.computed_status === "recovered"
                  ? "Loan fully recovered"
                  : "Full repayment awaited"
              }
              time={
                loan.recovered_at
                  ? new Date(loan.recovered_at).toLocaleString("en-IN")
                  : "—"
              }
            />
          </ol>
        </div>
      </main>
      <Footer />
    </div>
  );
}

function TimelineDot({ done, label, time }) {
  return (
    <li className="ml-4">
      <div
        className={`absolute -left-[7px] mt-1 h-3 w-3 rounded-full border-2 ${
          done
            ? "bg-indigo-500 border-indigo-300 shadow-[0_0_10px_rgba(99,102,241,0.6)]"
            : "bg-neutral-800 border-neutral-600"
        }`}
      />
      <div className="text-sm text-neutral-100">{label}</div>
      <div className="text-xs text-neutral-500 mt-0.5">{time}</div>
    </li>
  );
}

function ReminderCard({ loan, outstanding, shareUrl, tone, setTone, onLogged }) {
  const [sending, setSending] = useState(false);
  const message = buildReminderMessage({
    tone,
    borrowerName: loan.borrower_name,
    lenderName: loan.lender_name,
    outstanding,
    dueDate: loan.due_date,
    loanUrl: shareUrl,
  });

  const waHref = loan.borrower_phone
    ? `https://wa.me/${loan.borrower_phone.replace(/\D/g, "")}?text=${encodeURIComponent(message)}`
    : `https://wa.me/?text=${encodeURIComponent(message)}`;

  const send = async (e) => {
    setSending(true);
    try {
      const { data } = await api.post(`/loans/${loan.id}/reminders`, {
        tone,
        channel: "whatsapp",
        message,
      });
      onLogged(data.reminder);
      toast.success("Reminder logged · opening WhatsApp");
    } catch (err) {
      e.preventDefault();
      toast.error(apiErrorMessage(err));
    } finally {
      setSending(false);
    }
  };

  const remindersCount = (loan.reminders || []).length;
  const lastRemindedStr = loan.last_reminded_at
    ? new Date(loan.last_reminded_at).toLocaleString("en-IN")
    : null;
  const isOverdue = loan.computed_status === "overdue";

  const toneOptions = [
    { key: "friendly", label: "Friendly", tid: LOAN.reminderToneFriendly },
    { key: "firm", label: "Firm", tid: LOAN.reminderToneFirm },
    { key: "final", label: "Final", tid: LOAN.reminderToneFinal },
  ];

  return (
    <div className="lele-card p-6 md:p-8 mb-8">
      <div className="flex items-start justify-between gap-4 mb-3 flex-wrap">
        <div>
          <div className="text-[11px] uppercase tracking-[0.25em] text-neutral-500 flex items-center gap-2">
            <BellRinging size={13} weight="fill" className="text-amber-400" />
            {isOverdue ? "Overdue reminder" : "Send a nudge"}
          </div>
          <div className="font-heading text-xl mt-1">
            {isOverdue
              ? `${loan.borrower_name} is overdue`
              : `Remind ${loan.borrower_name}`}
          </div>
        </div>
        {remindersCount > 0 && (
          <div className="text-right">
            <div className="text-[11px] uppercase tracking-[0.2em] text-neutral-500">
              Reminders sent
            </div>
            <div className="text-sm text-neutral-200">{remindersCount}</div>
            {lastRemindedStr && (
              <div className="text-[11px] text-neutral-500">Last {lastRemindedStr}</div>
            )}
          </div>
        )}
      </div>

      <div className="flex gap-2 mb-4 flex-wrap">
        {toneOptions.map((t) => (
          <button
            key={t.key}
            data-testid={t.tid}
            onClick={() => setTone(t.key)}
            type="button"
            className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${
              tone === t.key
                ? "border-indigo-400 bg-indigo-500/10 text-indigo-200"
                : "border-white/10 text-neutral-400 hover:border-white/25 hover:text-neutral-100"
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      <div className="rounded-xl border border-white/5 bg-white/[0.02] p-4 text-sm text-neutral-300 whitespace-pre-wrap leading-relaxed max-h-56 overflow-y-auto mb-4">
        {message}
      </div>

      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="text-xs text-neutral-500">
          {loan.borrower_phone
            ? `Will open WhatsApp for +${loan.borrower_phone.replace(/\D/g, "")}`
            : "No borrower phone saved — WhatsApp will open with the message; you'll pick the contact."}
        </div>
        <a
          data-testid={LOAN.sendReminderBtn}
          href={waHref}
          target="_blank"
          rel="noreferrer"
          onClick={send}
          className="flex items-center gap-2 rounded-full px-5 py-3 text-sm font-medium text-white"
          style={{
            background: "linear-gradient(180deg,#25D366 0%,#128C7E 100%)",
            boxShadow: "0 8px 24px rgba(37,211,102,0.35)",
          }}
        >
          <WhatsappLogo size={16} weight="fill" />
          {sending ? "Sending…" : "Send on WhatsApp"}
        </a>
      </div>
    </div>
  );
}
